// The DSCF build flow as a graph read from the data, never drawn by hand:
// lanes (task groups) × tasks and their steps × shelves, joined by each
// shelf's filledBy/drawnBy and each task's fills/draws. Used by the board's
// flow slide, /tasks and Settings.
import type { ShelfRow, TaskRow } from "./types";

/** Andrea's DSCF chart, top to bottom. Chassis is where the line starts (downstairs). */
export const LANE_ORDER = ["Brain", "Camera module", "Chassis", "Back cover", "USB-C", "Display assembly", "Front cover", "Final assembly"] as const;

export interface FlowStep {
  code: string;
  name: string;
  minutesPerUnit: number | null;
  station: string | null;
}
export interface FlowTask {
  task: TaskRow;
  steps: FlowStep[];
  /** A cure is a wait with no labour before the next task may draw the output. */
  cure: string | null;
  fills: ShelfRow[];
  /** Shelves it takes from that are filled in another lane. */
  drawsFromElsewhere: ShelfRow[];
}
export interface FlowLane {
  group: string;
  floor: string | null;
  note: string | null;
  /** Shelves on this line filled by a task in another lane (S5 backs, from A2). */
  inbound: ShelfRow[];
  tasks: FlowTask[];
}
export interface FlowGraph {
  lanes: FlowLane[];
  /** After final assembly: S16, then provisioning, then S18 pass / S19 corrections. */
  outputs: { ready: ShelfRow | null; pass: ShelfRow | null; fail: ShelfRow | null };
}

const norm = (s: string | null | undefined) => (s ?? "").trim().toLowerCase();

/** Tasks in a lane ordered so a task comes after the ones that fill what it draws. */
function orderTasks(tasks: TaskRow[]): TaskRow[] {
  const left = [...tasks];
  const out: TaskRow[] = [];
  while (left.length) {
    const i = left.findIndex((t) => !left.some((u) => u !== t && u.fills.some((f) => t.draws.some((d) => d.shelf === f))));
    out.push(...left.splice(i < 0 ? 0 : i, 1));
  }
  return out;
}

export function buildFlowGraph(tasks: TaskRow[], shelves: ShelfRow[]): FlowGraph {
  const shelf = (code: string) => shelves.find((s) => s.code === code) ?? null;
  // The composite grid task (D2/D3) repeats D2 and D3; the flow shows the real steps once.
  const real = tasks.filter((t) => !t.code.includes("/"));
  const groups = [...LANE_ORDER, ...new Set(real.map((t) => t.group ?? "Other").filter((g) => !LANE_ORDER.includes(g as never)))];
  const laneOf = (t: TaskRow) => t.group ?? "Other";

  const lanes: FlowLane[] = groups.map((group) => {
    const own = orderTasks(real.filter((t) => norm(laneOf(t)) === norm(group)));
    const flowTasks: FlowTask[] = own.map((t) => ({
      task: t,
      steps: t.steps.map((s) => ({ code: s.code, name: s.name, minutesPerUnit: s.minutesPerUnit, station: s.station })),
      cure: t.cure,
      fills: t.fills.map(shelf).filter((s): s is ShelfRow => !!s && !s.isOutput && (s.countMode === "number" || norm(s.line) === norm(group))),
      drawsFromElsewhere: t.draws.map((d) => shelf(d.shelf)).filter((s): s is ShelfRow => {
        if (!s) return false;
        const filler = real.find((u) => u.code === s.filledBy);
        return !!filler && norm(laneOf(filler)) !== norm(group);
      })
    }));
    const shown = new Set(flowTasks.flatMap((f) => f.fills.map((s) => s.code)));
    const inbound = shelves.filter((s) => norm(s.line) === norm(group) && !s.isOutput && !shown.has(s.code));
    const floors = [...flowTasks.flatMap((f) => f.fills), ...inbound].map((s) => s.floor).filter(Boolean) as string[];
    const floor = floors.length ? floors.sort((a, b) => floors.filter((x) => x === b).length - floors.filter((x) => x === a).length)[0] : null;
    return { group, floor, note: group === "Chassis" ? "The line starts here" : null, inbound, tasks: flowTasks };
  }).filter((l) => l.tasks.length || l.inbound.length);

  const out = shelves.filter((s) => s.isOutput);
  const byName = (re: RegExp) => out.find((s) => re.test(s.name)) ?? null;
  return { lanes, outputs: { ready: byName(/ready|provision/i) ?? shelf("S16"), pass: byName(/pass/i) ?? shelf("S18"), fail: byName(/fail|correction/i) ?? shelf("S19") } };
}

/** "≈3.0 min" */
export const minutes = (m: number | null) => (m == null ? null : `≈${m.toFixed(1)} min`);
